import React, { useContext } from "react";
import { useHistory } from "react-router";
import { Formik } from "formik";
import * as Yup from "yup";
import firebase from "../../firebase";
import { SnackbarAlert } from "../SnackbarAlert";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";
import InputAdornment from "@material-ui/core/InputAdornment";

const useStyles = makeStyles((theme) => ({
  buttonRegister: {
    textTransform: "none",
    color: theme.palette.common.white,
    background: theme.palette.warning.light,
    padding: 13,
    borderRadius: 8,
    fontSize: "1.2em",
    letterSpacing: 1,
    "&:hover": {
      background: theme.palette.warning.main,
    },
  },
  userLink: {
    color: theme.palette.primary.main,
  },
  input: {
    margin: 5,
  },
  upload_input: {
    width: "0.1px",
    height: "0.1px",
    opacity: 0,
    overflow: "hidden",
    position: "absolute",
    zIndex: -1,
  },
  upload_box: {
    color: theme.palette.text.secondary,
    textTransform: "none",
    width: "100%",
    padding: theme.spacing(2, 2),
    boxSizing: "border-box",
    boxShadow: "0 0 0 0 0",
    backgroundColor: "hsl(0, 0%, 97%)",
    resize: "none",
    borderRadius: 16,
    border: `2px dashed ${theme.palette.text.secondary}`,
  },
  upload_box_error: {
    color: theme.palette.error.main,
    textTransform: "none",
    width: "100%",
    padding: theme.spacing(2, 2),
    boxSizing: "border-box",
    boxShadow: "0 0 0 0 0",
    backgroundColor: "hsl(0, 0%, 97%)",
    resize: "none",
    borderRadius: 16,
    border: `2px dashed ${theme.palette.error.main}`,
  },
  upload_box_helperText: {
    color: "rgba(0, 0, 0, 0.54)",
    marginLeft: "14px",
    marginRight: "14px",
    marginTop: "4px",
    margin: 0,
    fontSize: "0.75rem",
    textAlign: "left",
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: "0.03333em",
  },
}));

const DoctorRegisterForm = () => {
  const history = useHistory();
  const classes = useStyles();

  const createAccount = firebase.functions().httpsCallable("createUser");

  const { setSnackbarState } = useContext(SnackbarAlert);

  const FILE_SIZE = 5 * 1000000;
  const SUPPORTED_FORMATS = [
    "application/pdf",
    "image/jpg",
    "image/png",
    "image/jpeg",
  ];

  return (
    <Formik
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        specialty: "",
        createPassword: "",
        country: {
          name: "Philippines",
          code: "+63 ",
        },
        subsCheckbox: false,
        file: null,
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().required("Please input your first name"),
        lastName: Yup.string().required("Please input your last name"),
        email: Yup.string()
          .required("Please input your email")
          .email("Invalid format"),
        mobileNumber: Yup.number()
          .max(9999999999, "Invalid mobile number")
          .required("Please input your mobile number"),
        specialty: Yup.string().required("Please input your specialty"),
        createPassword: Yup.string()
          .required("Please input your password")
          .min(6, "Password should have a minimum of 6 characters"),
        loginCheckbox: Yup.boolean(),
        file: Yup.mixed()
          .required("Please attach required file")
          .test(
            "fileFormat",
            "Unsupported format!",
            (value) => value && SUPPORTED_FORMATS.includes(value.type)
          )
          .test(
            "fileSize",
            "File too large!",
            (value) => value && value.size <= FILE_SIZE
          ),
      })}
      onSubmit={async (values, actions) => {
        try {
          const filename = values.file.name;
          const extension = filename.slice(filename.lastIndexOf("."));
          const uid = await createAccount({
            email: values.email,
            mobileNumber: values.country.code + values.mobileNumber,
            password: values.createPassword,
            firstName: values.firstName,
            lastName: values.lastName,
            fullName: values.firstName + " " + values.lastName,
            country: values.country.name,
            specialty: values.specialty,
            forDoctor: true,
          });
          await firebase
            .storage()
            .ref(`doctorCertificates/${uid.data + extension}`)
            .put(values.file);
          const imageUrl = await firebase
            .storage()
            .ref(`doctorCertificates/${uid.data + extension}`)
            .getDownloadURL();
          await firebase
            .database()
            .ref(`users/${uid.data}`)
            .child("certificateUrl")
            .set(imageUrl);
          actions.resetForm();
          setSnackbarState({
            state: true,
            message: "Account creation successful! 👍",
            status: "info",
          });
        } catch (error) {
          actions.resetForm();
          //console.log(error);
          setSnackbarState({
            state: true,
            message: error.message,
            status: "error",
          });
        }
      }}
    >
      {(formik) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          noValidate
        >
          <Grid container spacing={1}>
            <Grid item xs={5}>
              <Box textAlign="left" fontWeight="fontWeightBold" fontSize={26}>
                Register as a Doctor
              </Box>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              <Box textAlign="right" fontSize={13} lineHeight={3}>
                Not a doctor?&nbsp;
                <Link
                  href="#"
                  className={classes.userLink}
                  onClick={(e) => {
                    e.preventDefault();
                    history.push("/login");
                  }}
                >
                  Register Here
                </Link>
                .
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="firstName"
                name="firstName"
                placeholder="First Name"
                margin="dense"
                variant="outlined"
                className={classes.input}
                {...formik.getFieldProps("firstName")}
                error={formik.errors.firstName && formik.touched.firstName}
                helperText={
                  formik.errors.firstName && formik.touched.firstName
                    ? formik.errors.firstName
                    : "First Name"
                }
                disabled={formik.isSubmitting}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                margin="dense"
                variant="outlined"
                className={classes.input}
                {...formik.getFieldProps("lastName")}
                error={formik.errors.lastName && formik.touched.lastName}
                helperText={
                  formik.errors.lastName && formik.touched.lastName
                    ? formik.errors.lastName
                    : "Last Name"
                }
                disabled={formik.isSubmitting}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="email"
                name="email"
                placeholder="Email ID"
                margin="dense"
                variant="outlined"
                className={classes.input}
                {...formik.getFieldProps("email")}
                error={formik.errors.email && formik.touched.email}
                helperText={
                  formik.errors.email && formik.touched.email
                    ? formik.errors.email
                    : "Email ID"
                }
                disabled={formik.isSubmitting}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="specialty"
                name="specialty"
                placeholder="Specialty"
                margin="dense"
                variant="outlined"
                className={classes.input}
                {...formik.getFieldProps("specialty")}
                error={formik.errors.email && formik.touched.email}
                helperText={
                  formik.errors.email && formik.touched.email
                    ? formik.errors.email
                    : "Specialty"
                }
                disabled={formik.isSubmitting}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <input
                accept="application/pdf,image/*"
                id="file"
                name="file"
                type="file"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  formik.setFieldValue("file", e.currentTarget.files[0]);
                }}
                className={classes.upload_input}
              />
              <label htmlFor="file">
                <Button
                  variant="text"
                  component="span"
                  className={
                    formik.errors.file && formik.touched.file
                      ? classes.upload_box_error
                      : classes.upload_box
                  }
                  disabled={formik.isSubmitting}
                  fullWidth
                >
                  {formik.errors.file && formik.touched.file
                    ? formik.errors.file
                    : formik.values.file && formik.values.file.name
                    ? formik.values.file.name
                    : "Click to attach doctor certificate..."}
                </Button>
              </label>
              <label className={classes.upload_box_helperText}>
                {formik.errors.file && formik.touched.file
                  ? ""
                  : formik.values.file && formik.values.file.name
                  ? "Verification may take up to 2-3 business days"
                  : "File must be in pdf or image format"}
              </label>
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="country"
                name="country"
                placeholder="Country"
                margin="dense"
                variant="outlined"
                disabled
                className={classes.input}
                {...formik.getFieldProps("country.name")}
                helperText="Country"
                fullWidth
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                id="mobileNumber"
                name="mobileNumber"
                placeholder="Mobile Number"
                margin="dense"
                variant="outlined"
                className={classes.input}
                {...formik.getFieldProps("mobileNumber")}
                error={
                  formik.errors.mobileNumber && formik.touched.mobileNumber
                }
                helperText={
                  formik.errors.mobileNumber && formik.touched.mobileNumber
                    ? formik.errors.mobileNumber
                    : "Mobile Number"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formik.values.country.code}
                    </InputAdornment>
                  ),
                }}
                disabled={formik.isSubmitting}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="createPassword"
                name="createPassword"
                placeholder="Create Password"
                margin="dense"
                variant="outlined"
                type="password"
                className={classes.input}
                {...formik.getFieldProps("createPassword")}
                error={
                  formik.errors.createPassword && formik.touched.createPassword
                }
                helperText={
                  formik.errors.createPassword && formik.touched.createPassword
                    ? formik.errors.createPassword
                    : "Create Password"
                }
                disabled={formik.isSubmitting}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="subsCheckbox"
                    {...formik.getFieldProps("subsCheckbox")}
                    disabled={formik.isSubmitting}
                  />
                }
                label={
                  <Box textAlign="left" fontSize={13}>
                    Receive relevant offers and promotional communication from
                    Life Doctor
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Box textAlign="left" fontSize={11}>
                By signing up. I agree to the&nbsp;
                <Link
                  href="https://life-doc.web.app/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  terms
                </Link>
                .
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                className={classes.buttonRegister}
                disabled={formik.isSubmitting}
                type="submit"
                fullWidth
              >
                Register
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default DoctorRegisterForm;
